"use client";

import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { io, Socket } from "socket.io-client";

// 1. Define the shape of our context
interface SocketContextType {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextType>({
  socket: null,
  isConnected: false,
});

// Custom hook to use the socket easily in other components
export const useSocket = () => useContext(SocketContext);

export const SocketProvider = ({ children }: { children: React.ReactNode }) => {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  // Use a ref to keep track of the socket instance for the cleanup function
  const socketRef = useRef<Socket | null>(null);

  useEffect(() => {
    // Initialize socket
    // IMPORTANT: In Next.js, client-side env vars MUST start with NEXT_PUBLIC_
    const backendUrl = process.env.NEXT_PUBLIC_BACKEND_URL ?? "http://localhost:4000";
    const frontendToken = process.env.NEXT_PUBLIC_FRONTEND_TOKEN ?? "change_me_pls";
    const socketInstance = io(backendUrl, {
      auth: {
        type: "frontend",
        token: frontendToken,
      },
      transports: ["websocket"],
    });

    socketRef.current = socketInstance;
    setSocket(socketInstance);

    socketInstance.on("connect", () => {
      console.log("✅ Socket Connected");
      setIsConnected(true);
    });

    socketInstance.on("disconnect", (reason) => {
      console.log("❌ Socket Disconnected:", reason);
      setIsConnected(false);
    });

    socketInstance.on("connect_error", (err) => {
      console.error("⚠️ Connection Error:", err);
    });

    // Cleanup
    return () => {
      console.log("--- CLEANING UP SOCKET ---");
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
    };
  }, []); // Empty array ensures this only runs ONCE on mount

  return <SocketContext.Provider value={{ socket, isConnected }}>{children}</SocketContext.Provider>;
};
